import { type KeyboardEvent, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";
import { Icon } from "./Icon";
import { placeBeside } from "./place";

interface TagGroup {
  label: string;
  tags: readonly string[];
}

/** A tag as Flashroom stores it: lowercase words joined by hyphens ("Hot lead" is "hot-lead"). */
export function normalizeTag(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * The tag picker: one button showing the chosen tag; it opens a searchable list grouped as Flashroom's own tags
 * and the host's, and a name that is not a tag yet can be added from the search box.
 */
export function TagPicker({
  value,
  groups,
  onChange,
  onAdd,
  label,
  placeholder = "Pick a tag",
}: {
  value: string;
  groups: readonly TagGroup[];
  onChange: (tag: string) => void;
  /** Called with a new tag's name. Without it the list only picks existing tags. */
  onAdd?: (tag: string) => void;
  /** What the tag is for, read aloud: "Starts when someone gets the tag". */
  label: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [spot, setSpot] = useState<{ top: number; left: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setQuery("");
    setSpot(null);
    if (refocus) button.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !button.current || !pop.current) return;
    setSpot(placeBeside(button.current.getBoundingClientRect(), { width: pop.current.offsetWidth, height: pop.current.offsetHeight }));
  }, [open]);

  // Why after it is placed: the list is hidden while it is measured, and a hidden box cannot take focus.
  useEffect(() => {
    if (open && spot) input.current?.focus({ preventScroll: true });
  }, [open, spot]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!pop.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node)) close(false);
    };
    const gone = () => close(false);
    document.addEventListener("mousedown", away);
    addEventListener("resize", gone);
    return () => {
      document.removeEventListener("mousedown", away);
      removeEventListener("resize", gone);
    };
  }, [open, close]);

  const q = query.trim().toLowerCase().replace(/\s+/g, "-");
  const name = normalizeTag(query);
  const known = groups.some((g) => g.tags.includes(name));
  const shown = groups.map((g) => ({ label: g.label, tags: g.tags.filter((t) => !q || t.includes(q)) })).filter((g) => g.tags.length);
  const canAdd = !!onAdd && !!name && !known;

  const pick = (tag: string) => {
    onChange(tag);
    close(true);
  };
  const add = () => {
    if (!canAdd || !onAdd) return;
    onAdd(name);
    pick(name);
  };

  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    const opts = [...(pop.current?.querySelectorAll<HTMLButtonElement>("[data-tag]") ?? [])];
    const at = opts.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      (opts[at + 1] ?? opts[0])?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (at <= 0) input.current?.focus();
      else opts[at - 1]?.focus();
    } else if (e.key === "Enter" && e.target === input.current) {
      e.preventDefault();
      const all = shown.flatMap((g) => g.tags);
      if (all.includes(name)) pick(name);
      else if (all.length === 1 && !canAdd) pick(all[0]);
      else if (canAdd) add();
      else if (all.length === 1) pick(all[0]);
    } else if (e.key === "Tab") close(false);
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        className={cx("tsel", !value && "empty")}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${label}: ${value || "none yet"}`}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <Icon name="tag" />
        <span className="tsel-v">{value || placeholder}</span>
        <Icon name="down" />
      </button>
      {open
        ? createPortal(
            <div ref={pop} className="tsel-pop" style={spot ?? { visibility: "hidden", top: 0, left: 0 }} onKeyDown={keys}>
              <div className="tsel-q">
                <Icon name="search" />
                <input dir="auto"
                  ref={input}
                  className="inp"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={onAdd ? "Find or add a tag" : "Find a tag"}
                  aria-label={onAdd ? "Find or add a tag" : "Find a tag"}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <div className="tsel-body" id={listId} role="listbox" aria-label={label}>
                {shown.map((g) => (
                  <div key={g.label} className="tsel-g" role="group" aria-label={g.label}>
                    <p>{g.label}</p>
                    <div className="tsel-os">
                      {g.tags.map((t) => (
                        <button
                          key={t}
                          type="button"
                          role="option"
                          aria-selected={t === value}
                          data-tag={t}
                          className={cx("tsel-o", t === value && "on")}
                          onClick={() => pick(t)}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {canAdd ? (
                  <button type="button" className="tsel-new" data-tag={name} onClick={add}>
                    <Icon name="plus" />
                    {`Add “${name}”`}
                  </button>
                ) : null}
                {!shown.length && !canAdd ? <p className="tsel-none">No tags</p> : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
